import type { BotIdTags } from "@homeinstead/analytics";
import type { CareSeekerLeadFormResponse } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { LeadResponse } from "~www/types/forms/lead-response";

import { useEffect, useRef } from "react";

const SUBMISSION_ERROR = "Submission Error";

export type CareSeekerSubmissionTrackingOptions = {
  organizationNumber: number | undefined;
  trackSuccess: (leadId: string, tags: BotIdTags) => void;
  trackError: (
    type: "duplicate" | "server-error" | "validation-error",
    tags: BotIdTags,
    validationErrors?: Record<string, string>,
    spamBlockReason?: string,
  ) => void;
  errorContext: string;
};

/**
 * Tracks success/error analytics when submission state changes.
 * Call alongside useCareSeekerSubmissionResult so the UI stays pure from state.
 */
export function useCareSeekerSubmissionTracking(
  state: LeadResponse<CareSeekerLeadFormResponse> | null,
  options: CareSeekerSubmissionTrackingOptions,
): void {
  const { organizationNumber, trackSuccess, trackError, errorContext } =
    options;

  const isSuccessTracked = useRef(false);
  const isErrorTracked = useRef(false);
  const isValidationErrorTracked = useRef(false);

  useEffect(() => {
    if (state?.ok && !state?.data?.isError) {
      const leadId = state.data?.id;
      if (!isSuccessTracked.current && leadId) {
        isSuccessTracked.current = true;
        trackSuccess(leadId, state.tags);
      }
      return;
    }
    if (state?.ok && state?.data?.isError) {
      if (!isErrorTracked.current) {
        isErrorTracked.current = true;
        const errors = state.data.errors ?? [];
        const isDuplicate = errors.some((e) => e.code === "duplicate");
        const errorType = isDuplicate ? "duplicate" : "server-error";
        if (!isDuplicate) {
          console.error({
            error: errors,
            message: `${errorContext} - ${SUBMISSION_ERROR}: ${errorType}`,
          });
        }
        trackError(errorType, state.tags);
      }
      return;
    }
    if (state !== null && !state.ok && !isValidationErrorTracked.current) {
      isValidationErrorTracked.current = true;
      if (state.validationErrors) {
        trackError(
          "validation-error",
          state.tags,
          state.validationErrors,
          state.spamBlockReason,
        );
      } else {
        trackError("server-error", state.tags);
      }
    }
  }, [state, organizationNumber, trackSuccess, trackError, errorContext]);
}
